import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CatalogThumb } from "@/components/CardImage";
import { Search, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const GAMES = [{ v: "pokemon", l: "Pokémon" }, { v: "one_piece", l: "One Piece" }];

// Lets user search the catalog and select a card, or create a new one (with duplicate check).
export function CardPicker({ onSelect, selectedId }) {
  const [game, setGame] = useState("pokemon");
  const [q, setQ] = useState("");
  const [expansions, setExpansions] = useState([]);
  const [expansionId, setExpansionId] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", expansion_name: "", expansion_code: "", card_number: "", variant: "", rarity: "" });
  const [dupes, setDupes] = useState([]);

  useEffect(() => {
    setExpansionId("");
    api.get("/catalog/expansions", { params: { game } }).then((r) => setExpansions(r.data)).catch(() => setExpansions([]));
  }, [game]);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalog/cards", { params: { game, q: q || undefined, expansion_id: expansionId || undefined, page_size: 12 } });
      setResults(data.items);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [game, q, expansionId]);

  useEffect(() => { const t = setTimeout(search, 250); return () => clearTimeout(t); }, [search]);

  const checkDupes = async () => {
    if (!form.name.trim()) return;
    try { const { data } = await api.post("/catalog/cards/find-duplicates", { game, name: form.name }); setDupes(data.candidates); }
    catch { /* ignore */ }
  };

  const createCard = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome della carta."); return; }
    try {
      const { data } = await api.post("/catalog/cards", { game, ...form });
      if (data.reused) toast.info("Carta esistente riutilizzata dal catalogo.");
      else if (data.is_draft) toast.warning(`Salvata come bozza: completa ${(data.missing || []).join(", ")} per usarla negli scambi.`);
      else toast.success("Carta aggiunta al catalogo.");
      onSelect(data.card);
      setCreating(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="card-picker">
      <div className="flex gap-2">
        <Select value={game} onValueChange={setGame}>
          <SelectTrigger className="w-36 bg-[#0B1020] border-[#2A3655]" data-testid="picker-game"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
            {GAMES.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-[#94A3B8]" />
          <Input data-testid="picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca una carta per nome…" className="bg-[#0B1020] border-[#2A3655] pl-9" />
        </div>
      </div>

      {!creating && expansions.length > 0 && (
        <Select value={expansionId || "all"} onValueChange={(v) => setExpansionId(v === "all" ? "" : v)}>
          <SelectTrigger className="bg-[#0B1020] border-[#2A3655]" data-testid="picker-expansion"><SelectValue placeholder="Tutte le espansioni" /></SelectTrigger>
          <SelectContent className="max-h-64 bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
            <SelectItem value="all">Tutte le espansioni</SelectItem>
            {expansions.map((e) => <SelectItem key={e.id} value={e.id}>{e.code ? `${e.name} (${e.code})` : e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {!creating && (
        <>
          <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
            {loading && <p className="col-span-full py-4 text-center text-sm text-[#94A3B8]">Ricerca…</p>}
            {!loading && results.length === 0 && <p className="col-span-full py-4 text-center text-sm text-[#94A3B8]">Nessuna carta trovata.</p>}
            {results.map((c) => (
              <button key={c.id} type="button" data-testid="picker-result" onClick={() => onSelect(c)}
                className={`rounded-lg border p-1.5 text-left transition-colors ${selectedId === c.id ? "border-[#2DD4BF] bg-[#1C2640]" : "border-[#2A3655] hover:border-[#2DD4BF]/60"}`}>
                <CatalogThumb card={c} className="mb-1" />
                <p className="line-clamp-1 text-[11px] font-semibold text-[#F8FAFC]">{c.name}</p>
                <p className="line-clamp-1 text-[10px] text-[#94A3B8]">{c.expansion_name || c.expansion_code || "Espansione n/d"}</p>
                {selectedId === c.id && <span className="flex items-center gap-1 text-[10px] text-[#2DD4BF]"><Check className="h-3 w-3" />Selezionata</span>}
              </button>
            ))}
          </div>
          <Button type="button" variant="outline" onClick={() => setCreating(true)} data-testid="picker-add-new"
            className="w-full border-[#2A3655] text-[#CBD5E1] hover:bg-[#1C2640]">
            <Plus className="mr-2 h-4 w-4" />Non la trovi? Inseriscila
          </Button>
        </>
      )}

      {creating && (
        <div className="space-y-2 rounded-lg border border-[#2A3655] bg-[#0B1020] p-3">
          <p className="text-xs text-[#94A3B8]">Inserimento assistito: cerchiamo eventuali duplicati prima di creare una nuova voce.</p>
          <div>
            <Label className="text-[#CBD5E1] text-xs">Nome *</Label>
            <Input data-testid="new-card-name" value={form.name} onBlur={checkDupes} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#141B2D] border-[#2A3655]" />
          </div>
          {dupes.length > 0 && (
            <div className="rounded-md border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 p-2">
              <p className="mb-1 text-xs text-[#2DD4BF]">Carte simili già presenti — riutilizza:</p>
              {dupes.map((d) => (
                <button key={d.id} type="button" onClick={() => { onSelect(d); setCreating(false); }} className="block w-full rounded px-2 py-1 text-left text-xs text-[#F8FAFC] hover:bg-[#1C2640]">
                  {d.name} · {d.expansion_name || d.expansion_code || "—"} · {d.card_number || d.variant || ""}
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[#CBD5E1] text-xs">Espansione</Label><Input data-testid="new-card-exp" value={form.expansion_name} onChange={(e) => setForm({ ...form, expansion_name: e.target.value })} className="bg-[#141B2D] border-[#2A3655]" /></div>
            <div><Label className="text-[#CBD5E1] text-xs">Codice espansione</Label><Input value={form.expansion_code} onChange={(e) => setForm({ ...form, expansion_code: e.target.value })} className="bg-[#141B2D] border-[#2A3655]" /></div>
            <div><Label className="text-[#CBD5E1] text-xs">Numero carta</Label><Input data-testid="new-card-number" value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} className="bg-[#141B2D] border-[#2A3655]" /></div>
            <div><Label className="text-[#CBD5E1] text-xs">Variante</Label><Input value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} className="bg-[#141B2D] border-[#2A3655]" /></div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" onClick={createCard} data-testid="new-card-save" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Salva carta</Button>
            <Button type="button" variant="ghost" onClick={() => setCreating(false)} className="text-[#CBD5E1]">Annulla</Button>
          </div>
        </div>
      )}
    </div>
  );
}
